import { useEffect, useMemo, useState } from "react";
import {
  Typography,
  Button,
  AppBar,
  IconButton,
  Dialog,
  Toolbar,
  Alert,
  Snackbar,
  DialogContent,
  Stack,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeviceHubIcon from "@mui/icons-material/DeviceHub";
import MaterialReactTable from "material-react-table";
import {
  NotePatient,
  TelemonitoraggiPatient,
  RecapitiPatient,
  DispositiviPatient,
  ReportPatient,
  useGetPatientById,
  usePutPatientById,
} from "../../PatientComponents";
import { useAuthUser } from "react-auth-kit";
import { AddService } from "../../ServiceComponents";
import { LoadingError, LoadingPaper } from "../../LoadingComponents";
import columns from "./ViewPatientColumns";
import { useEditMode } from "../../EditableComponents";
import { TabsView } from "../../ReportComponents";
import AssociateDeviceDialog from "../DispositiviPatient/AssociateDeviceDialog";

function ViewPatient({ patient_id, org_id, closeModal, _editMode }) {
  // === web service queries/mutations ===
  const patientQuery = useGetPatientById(patient_id);
  const editModePatientMutation = usePutPatientById(patient_id);
  const authUser = useAuthUser();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (patientQuery.isSuccess) {
      setData([
        { row_type: "Recapiti", ...patientQuery.data },
        { row_type: "Telemonitoraggi Associati" },
        { row_type: "Dispositivi" },
        { row_type: "Misurazioni e Report" },
        { row_type: "Note e Documenti" },
      ]);
    }
  }, [patientQuery.data, patientQuery.isSuccess]);

  // === EditMode handling ===
  const {
    editMode,
    enterEditMode,
    cancelEditMode,
    confirmEditMode,
    updateEditState,
    setQueryKey,
  } = useEditMode({
    editModeEnabled: _editMode,
    updateMutation: editModePatientMutation,
    initialQueryKey: ["patients", patient_id],
  });

  useEffect(() => {
    if (_editMode) enterEditMode();
  }, []);

  // to handle AddService modal
  const [openAddService, setOpenAddService] = useState(false);
  const handleOpenAddService = () => {
    setOpenAddService(true);
  };
  const handleCloseAddService = () => {
    setOpenAddService(false);
  };

  const [openAssociateDevice, setOpenAssociateDevice] = useState(false);
  const handleOpenAssociateDevice = () => setOpenAssociateDevice(true);
  const handleCloseAssociateDevice = () => setOpenAssociateDevice(false);

  const isDoctor = authUser()?.role === 3;
  const tableColumns = columns(editMode, updateEditState, {
    showDoctor: !isDoctor,
  });

  return (
    <>
      {patientQuery.isSuccess && (
        <>
          <Dialog open={openAddService} sx={{ width: "100%" }}>
            <AddService
              patient_id={patient_id}
              doctor_id={patientQuery.data.doctor_id}
              closeModal={handleCloseAddService}
            />
          </Dialog>
          <AssociateDeviceDialog
            open={openAssociateDevice}
            patient_id={patient_id}
            onClose={handleCloseAssociateDevice}
          />
        </>
      )}
      <AppBar sx={{ position: "relative" }}>
        <Toolbar>
          <IconButton //
            edge="start"
            color="inherit"
            onClick={closeModal}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {patientQuery.isLoading ? (
        <LoadingPaper />
      ) : patientQuery.isError ? (
        <LoadingError error={patientQuery.error} />
      ) : (
        data !== null && (
          <DialogContent>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h5" component="div">
              Visualizza paziente:{" "}
              {`${patientQuery.data.first_name} ${patientQuery.data.last_name}`}
            </Typography>

            <Stack spacing={2} direction={"column"}>
              <Box sx={{ width: "100%" }}>
                {/* Azioni */}
                <Box sx={{ float: "left" }}>
                  <Button // MODIFICA
                    style={{ margin: "5px" }}
                    color={editMode ? "success" : "warning"}
                    variant="contained"
                    onClick={editMode ? confirmEditMode : enterEditMode}
                  >
                    <EditIcon sx={{ marginRight: "5px" }} />
                    {editMode ? "Conferma" : "Modifica"}
                  </Button>
                  <Button // ADD SERVIZIO
                    style={{ margin: "5px" }}
                    color="primary"
                    onClick={handleOpenAddService}
                    disabled={
                      editMode || patientQuery.data.disclaimer === "anagrafica"
                    }
                    variant="contained"
                  >
                    <AddIcon sx={{ marginRight: "5px" }} />
                    Nuovo Telemonitoraggio
                  </Button>
                  <Button
                    style={{ margin: "5px" }}
                    color="primary"
                    onClick={handleOpenAssociateDevice}
                    disabled={editMode}
                    variant="contained"
                  >
                    <DeviceHubIcon sx={{ marginRight: "5px" }} />
                    Associa/disconnetti dispositivo
                  </Button>
                </Box>
              </Box>
              {/* Informazioni di base */}
              <MaterialReactTable
                columns={tableColumns}
                data={data}
                // table customization
                enableColumnActions={false}
                enableColumnFilters={false}
                enablePagination={false}
                enableSorting={false}
                enableBottomToolbar={false}
                initialState={{ expanded: true }}
                enableTopToolbar={false}
                muiTableBodyRowProps={{ hover: false }}
                renderDetailPanel={({ row }) => {
                  switch (row.original.row_type) {
                    case "Recapiti":
                      return (
                        <RecapitiPatient
                          _patient={patientQuery.data}
                          editMode={editMode}
                          setParentValue={updateEditState}
                        />
                      );

                    case "Telemonitoraggi Associati":
                      return <TelemonitoraggiPatient patient_id={patient_id} />;

                    case "Dispositivi":
                      return <DispositiviPatient patient_id={patient_id} />;

                    case "Misurazioni e Report":
                      return (
                        <TabsView patient_id={patient_id} org_id={org_id} />
                      );

                    case "Note e Documenti":
                      return (
                        <NotePatient
                          _patient={patientQuery.data}
                          editMode={editMode}
                          setParentValue={updateEditState}
                        />
                      );

                    default:
                      return null;
                  }
                }}
              />
            </Stack>
          </DialogContent>
        )
      )}
    </>
  );
}

export default ViewPatient;
